import { useEffect, useRef, useState } from "react";
import {
    Button,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    Stack,
    Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";

import { UUID } from "crypto";
import { RootState } from "@store/store";
import { DialogProps } from "@toolpad/core/useDialogs";

import { FinancialCompany } from "@interfaces/catalogs";
import { DialogoBaseProps } from "@interfaces/components";
import { useLazyGetCompaniesQuery } from "@services/api/companies";
import { setFilterListByCompanies, setFilterListByFinancials } from "@store/slices/componentsSlice";
import { CustomMultipleSelect } from "@components/CustomMultipleSelect";

export const CambiarVistaCompania = (props: DialogProps<DialogoBaseProps>) => {
    const dispatch = useDispatch();
    const hasFetched = useRef(false);

    const [financialCoSelected, setFinancialCoSelected] = useState<
        Array<Pick<FinancialCompany, "id" | "name" | "code">>
    >([]);

    const [values, setValues] = useState<Array<UUID>>([]);
    const [companies, setCompanies] = useState<Array<{ id: UUID; name: string; display_name: string }>>([]);

    const companiesUserAssigned = useSelector((state: RootState) => state.app.session.companies);
    const companiesFilters = useSelector((state: RootState) => state.components.lists.shared_filters.companies);

    const financialUserAssigned = useSelector((state: RootState) => state.app.session.financial_inc);
    const financialsFilters = useSelector((state: RootState) => state.components.lists.shared_filters.financials);

    const [triggerGetCompanies] = useLazyGetCompaniesQuery();

    const handleOnCompaniesChanged = async (_: UUID, selected: Array<any>) => {
        setValues(selected);

        let financialsTmp: Array<Pick<FinancialCompany, "id" | "name" | "code">> = [];

        if (selected.includes(companies.find((t) => t.name === "PYMIO"))) {
            financialsTmp = financialCoSelected;
        }

        if (selected.length > 0) {
            let financialsRelated: Array<Pick<FinancialCompany, "id" | "name" | "code">> = [];
            selected.forEach((companyId) => {
                if (companiesUserAssigned.find((t) => t.id === companyId && t.name === "PYMIO")) {
                } else {
                    financialUserAssigned
                        .filter((f) => f.company_id === companyId)
                        .forEach((financial: any) =>
                            financialsRelated.push({
                                id: financial.id,
                                name: financial.name,
                                code: financial.code,
                            }),
                        );
                }
            });

            setFinancialCoSelected(
                [...financialsTmp, ...financialsRelated].filter(
                    (financial, idx, self) => idx === self.findIndex((c) => c.id === financial.id),
                ),
            );
        }
    };

    const handleOnViewChangeClicked = async () => {
        let selectedCompanies = [];
        if (values.includes("0-0-0-0-0")) {
            selectedCompanies = companies;
        } else {
            selectedCompanies = companies.filter((company) => values.includes(company.id) === true);
        }

        dispatch(setFilterListByCompanies(selectedCompanies));
        dispatch(setFilterListByFinancials(financialCoSelected === undefined ? [] : financialCoSelected));
        // if (selectedCompanies.find((t) => t.name === "PYMIO")) {
        //     dispatch(setFilterListByFinancials(financialCoSelected === undefined ? [] : financialCoSelected));
        // } else {
        //     dispatch(setFilterListByFinancials([]));
        // }
        props.onClose();
    };

    const loadInfo = async () => {
        if (companiesUserAssigned.length === 0) {
            const companies = await triggerGetCompanies().unwrap();
            setCompanies(
                companies.map((company) => ({
                    id: company.id,
                    name: company.display_name,
                    display_name: company.display_name,
                })),
            );
        } else {
            setCompanies(
                companiesUserAssigned.map((company) => ({
                    id: company.id,
                    name: company.name,
                    display_name: company.display_name,
                })),
            );
        }

        setValues(companiesFilters.map((t) => t.id));

        if (financialsFilters) {
            setFinancialCoSelected(financialsFilters);
        }
    };

    useEffect(() => {
        if (hasFetched.current) return;

        loadInfo();

        hasFetched.current = true;
    }, [hasFetched]);

    return (
        <Dialog fullWidth={props.payload.fullWidth} maxWidth={props.payload.maxWidth} open={props.open}>
            <DialogTitle>
                <Typography variant="body1" color="main3" fontWeight={600}>
                    Seleccione la(s) compañia(s):
                </Typography>
            </DialogTitle>
            <DialogContent sx={{ px: 4 }}>
                <Stack rowGap={2}>
                    <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                        <InputLabel required>Compañias</InputLabel>
                        <CustomMultipleSelect
                            label="Compañias"
                            menuHeight={450}
                            personId={"0-0-0-0-0"}
                            defaultValue={values}
                            options={companies.map((company) => ({ id: company.id, name: company.display_name }))}
                            onChange={handleOnCompaniesChanged}
                        />
                    </FormControl>
                    <Collapse in={values.includes(companies.find((t) => t.name === "PYMIO")?.id ?? "")}>
                        <FormControl fullWidth sx={{ textAlign: "left" }} size="small">
                            <InputLabel required>Financieras</InputLabel>
                            <CustomMultipleSelect
                                label="Financieras"
                                menuHeight={350}
                                personId={"0-0-0-0-0"}
                                defaultValue={financialCoSelected.map((t) => t.id)}
                                options={financialUserAssigned.map((financial) => ({
                                    id: financial.id,
                                    name: financial.name,
                                }))}
                                onChange={async (_: UUID, selected: Array<any>) => {
                                    setFinancialCoSelected(
                                        financialUserAssigned.filter((t) => selected.includes(t.id)),
                                    );
                                }}
                            />
                        </FormControl>
                    </Collapse>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ mb: 2, justifyContent: "space-evenly" }}>
                <Button variant="blue_outlined" onClick={() => props.onClose()}>
                    Regresar
                </Button>
                <Button
                    variant="contained"
                    disabled={
                        values.length === 0 ||
                        (values.includes(companies.find((t) => t.name === "PYMIO")?.id ?? "") &&
                            financialCoSelected?.length === 0)
                    }
                    onClick={handleOnViewChangeClicked}
                >
                    Cambiar vista
                </Button>
            </DialogActions>
        </Dialog>
    );
};
